import { useState } from 'react';
import { MirrorFreshnessBanner } from '../components/common/MirrorFreshnessBanner';
import { useParams } from 'react-router-dom';
import { RowLink, ROW_LINK_HOST } from '../components/links/EntityLink';
import { realLinkProps } from '../components/links/realLinkProps';
import { useEntityLinkProps, useRouterNavigate } from '../components/links/useEntityLinkProps';
import {
  makeStyles,
  tokens,
  Card,
  Text,
  Title1,
  Title2,
  Badge,
  Button,
  Spinner,
  Tab,
  TabList,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Avatar,
  Tooltip,
  Checkbox,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Input,
  Textarea,
  Dropdown,
  Option,
} from '@fluentui/react-components';
import { LoadFailed } from '../components/common/LoadFailed';
import {
  ArrowLeftRegular,
  PersonRegular,
  DocumentSearchRegular,
  TaskListLtr24Regular,
  CheckmarkCircleRegular,
  ClockRegular,
  AlertRegular,
  OpenRegular,
  LinkRegular,
  AddRegular,
  DeleteRegular,
  CheckboxCheckedRegular,
  TranslateRegular,
} from '@fluentui/react-icons';
import { useBookkeepingClients } from '../hooks/useClients';
import { useClientReviews } from '../hooks/useReviews';
import { useActionItems } from '../hooks/useMeetings';
import { useClientSettings, useDefaultChecklistItems, useToggleDefaultItem, useAddCustomItem, useDeleteCustomItem } from '../hooks/useClientSettings';
import { formatCalendarDate, formatPeriodRange } from '../utils/calendarDate';
import { StatusBadge, statusDisplayName } from '../components/common/StatusBadge';
import { StaffRoleBadges } from '../components/common/StaffBadge';
import { PageSkeleton } from '../components/common/PageSkeleton';
import { ClientAnnouncementBanner } from '../components/common/ClientAnnouncementBanner';
import { statusLabel as actionItemStatusLabel } from '../utils/actionItemStatus';
import i18n, { useTranslation } from '../i18n';
import { formatInstant } from '../i18n/dates';
import { taskWindowLabel, sinceStartOfYear } from '../utils/taskWindow';
import { ClientLanguageBadge } from '../components/common/ClientLanguageBadge';

const useStyles = makeStyles({
  page: {
    // Full width beside the nav, with the shell's own 24px padding only (polish pass, readiness criterion 13, P1/P2).
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '24px',
    marginBottom: '24px',
  },
  backButton: {
    marginBottom: '16px',
  },
  avatar: {
    width: '80px',
    height: '80px',
    fontSize: '32px',
  },
  headerInfo: {
    flex: 1,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    display: 'block',
    marginBottom: '4px',
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  tabContent: {
    marginTop: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px',
    color: tokens.colorNeutralForeground3,
  },
  infoRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '8px',
    flexWrap: 'wrap',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  infoLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

function getComplexityBadge(complexity?: string) {
  switch (complexity) {
    case 'high':
      return <Badge appearance="filled" color="danger">{i18n.t('complexity.label.high')}</Badge>;
    case 'medium':
      return <Badge appearance="filled" color="warning">{i18n.t('complexity.label.medium')}</Badge>;
    case 'low':
      return <Badge appearance="filled" color="success">{i18n.t('complexity.label.low')}</Badge>;
    default:
      return <Badge appearance="outline">-</Badge>;
  }
}

function getReviewStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge appearance="filled" color="success">{i18n.t('reviewStatus.completed')}</Badge>;
    case 'in-progress':
      return <Badge appearance="tint" color="brand">{i18n.t('reviewStatus.inProgress')}</Badge>;
    case 'pending':
      return <Badge appearance="outline" color="warning">{i18n.t('reviewStatus.pending')}</Badge>;
    default:
      return <Badge appearance="outline">{status}</Badge>;
  }
}

export function ClientDetailPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  // Real links (fleet ruling 2026-10-29): Back, the review rows and their Open buttons are <a href>.
  const clientsLink = useEntityLinkProps('/clients');
  const reviewsLink = useEntityLinkProps('/reviews');
  const routerNavigate = useRouterNavigate();
  const [selectedTab, setSelectedTab] = useState<string>('overview');
  const [checklistReviewType, setChecklistReviewType] = useState<'quarterly' | 'year-end'>('quarterly');
  const [showAddCustomDialog, setShowAddCustomDialog] = useState(false);
  const [newCustomTitle, setNewCustomTitle] = useState('');
  const [newCustomDescription, setNewCustomDescription] = useState('');
  const [newCustomCategory, setNewCustomCategory] = useState('custom');

  const { data: clients, isLoading: clientsLoading, error: clientsError, refetch: refetchClients } = useBookkeepingClients();
  const { data: allReviews, isLoading: reviewsLoading, error: reviewsError, refetch: refetchReviews } = useClientReviews();
  const { data: actionItemsData, isLoading: actionItemsLoading, error: actionItemsError, refetch: refetchActionItems } = useActionItems();
  const { data: clientSettings, isLoading: settingsLoading, error: settingsError, refetch: refetchSettings } = useClientSettings(id);
  const { data: defaultItems, error: defaultsError, refetch: refetchDefaults } = useDefaultChecklistItems(checklistReviewType);
  // A count is shown only once its list has loaded: "…" while loading, "—" when the load failed, never a 0
  // that is really "unknown" (P6, 2026-10-30).
  const countOf = (loaded: boolean, failed: unknown, n: number) => (loaded ? n : failed ? '—' : '…');
  const reviewsKnown = allReviews !== undefined;
  const actionItemsKnown = actionItemsData !== undefined;
  const toggleDefaultItemMutation = useToggleDefaultItem();
  const addCustomItemMutation = useAddCustomItem();
  const deleteCustomItemMutation = useDeleteCustomItem();

  // Find the client
  const client = clients?.find(c => c.cr8f7_bookkeepingid === id);

  // Filter reviews for this client
  const clientReviews = allReviews?.filter(r => r.bookkeepingId === id) || [];

  // "Reviewed through" = latest period end across this client's COMPLETED reviews only
  // (pending/in-progress/cancelled don't count). periodEnd is an ISO 'YYYY-MM-DD' string,
  // so lexical max is the chronological max. Empty → "Never reviewed".
  const reviewedThroughIso = clientReviews
    .filter(r => r.status === 'completed')
    .reduce<string | null>((max, r) => (max === null || r.periodEnd > max ? r.periodEnd : max), null);
  const reviewedThroughLabel = !reviewsKnown
    ? (reviewsError ? t('clientDetail.reviewHistoryNotLoaded') : '…')
    : reviewedThroughIso
      ? t('clientDetail.reviewedThrough', { date: formatCalendarDate(reviewedThroughIso) })
      : t('clientDetail.neverReviewed');

  // Filter action items for this client's bookkeeper
  const clientActionItems = actionItemsData?.items?.filter(
    item => item.sourceClientId === client?._cr8f7_client_value
  ) || [];

  if (clientsLoading) {
    return <PageSkeleton variant="detail" />;
  }

  // The client list failed: that is not "client not found".
  if (!clients && clientsError) {
    return (
      <div className={styles.page}>
        <Button as="a" appearance="subtle" icon={<ArrowLeftRegular />} {...clientsLink} className={styles.backButton}>
          {t('clientDetail.back')}
        </Button>
        <Card style={{ padding: '24px' }}>
          <LoadFailed onRetry={() => refetchClients()} />
        </Card>
      </div>
    );
  }

  if (!client) {
    return (
      <div className={styles.page}>
        <Button
          as="a"
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          {...clientsLink}
          className={styles.backButton}
        >
          {t('clientDetail.back')}
        </Button>
        <Card style={{ padding: '48px', textAlign: 'center' }}>
          <Title1>{t('clientDetail.notFound.title')}</Title1>
          <Text style={{ color: tokens.colorNeutralForeground3 }}>
            {t('clientDetail.notFound.body')}
          </Text>
        </Card>
      </div>
    );
  }

  const clientName = client['_cr8f7_client_value@OData.Community.Display.V1.FormattedValue'] || client.cr8f7_name || t('common.unknown');
  const bookkeeperName = client['_ownerid_value@OData.Community.Display.V1.FormattedValue'] || t('common.unassigned');
  const statusCategory = client.statusCategory;
  const statusLabel = statusDisplayName(statusCategory);
  const txns = client.cr8f7_avgmonthlytransactions || 0;
  const complexity = txns > 300 ? 'high' : txns > 100 ? 'medium' : 'low';
  const lockDate = client.cr8f7_transactionlockdatexero
    ? formatCalendarDate(client.cr8f7_transactionlockdatexero)
    : t('clientDetail.notSet');

  // Live task counts — the SAME windowed figures the Dashboard tile and Clients list show
  // (server-computed in ClientFunctions.GetClients via GetTaskCountsForAccountsAsync). Previously
  // this page rendered the stale stored Dataverse mirror (cr8f7_outstandingtask), so the same
  // "Outstanding" label meant a different number here than on the dashboard/list (SUP-11).
  // "Outstanding" = OPEN tasks created within the window; "Completed" = tasks completed within it.
  // The window's first DAY comes from the API and is worded here (src/utils/taskWindow).
  const taskPeriodLabel = taskWindowLabel(client.taskCountSince, client.taskCountPeriod, sinceStartOfYear());
  const outstandingTasks = client.filteredOutstandingTasks || 0;
  const completedTasks = client.filteredCompletedTasks || 0;
  const waitingOnClientTasks = client.cr8f7_waitingonclienttask || 0;
  // Stored Dataverse mirror, surfaced separately (legacy) so the figure isn't lost.
  const legacyOutstandingTasks = client.cr8f7_outstandingtask || 0;

  return (
    <div className={styles.page}>
      <MirrorFreshnessBanner />
      {/* Back Button */}
      <Button
        as="a"
        appearance="subtle"
        icon={<ArrowLeftRegular />}
        {...clientsLink}
        className={styles.backButton}
      >
        {t('clientDetail.back')}
      </Button>

      {/* Pinned client announcements — cannot-miss, above the header */}
      <ClientAnnouncementBanner companyId={client._cr8f7_client_value} />

      {/* Header */}
      <Card style={{ padding: '24px', marginBottom: '24px' }}>
        <div className={styles.header}>
          <Avatar
            name={clientName}
            size={72}
            color="colorful"
          />
          <div className={styles.headerInfo}>
            <Title1 style={{ marginBottom: '4px' }}>{clientName}</Title1>
            <Text
              className={styles.infoLabel}
              style={{ display: 'block', marginBottom: '8px' }}
            >
              {reviewedThroughLabel}
            </Text>
            <div className={styles.infoRow}>
              <div className={styles.infoItem}>
                <PersonRegular />
                <Text>{bookkeeperName}</Text>
                <StaffRoleBadges staffIdentifier={bookkeeperName} size="header" />
              </div>
              <StatusBadge category={statusCategory} />
              {getComplexityBadge(complexity)}
            </div>
            <div className={styles.infoRow} style={{ marginTop: '12px' }}>
              <div className={styles.infoItem}>
                <Text className={styles.infoLabel}>{t('clientDetail.lockDate')}</Text>
                <Text>{lockDate}</Text>
              </div>
              <div className={styles.infoItem}>
                <Text className={styles.infoLabel}>{t('clientDetail.avgTxns')}</Text>
                <Text>{txns.toLocaleString(i18n.language)}</Text>
              </div>
              {clientReviews.length > 0 && (
                <div className={styles.infoItem}>
                  <TranslateRegular />
                  <Text className={styles.infoLabel}>{t('clientDetail.language')}</Text>
                  {/* Live from the CRM record (stamped by the server), never a review's stored copy. */}
                  <ClientLanguageBadge language={clientReviews[0].clientLanguage} />
                </div>
              )}
              {client.cr8f7_xerolink && (
                <Tooltip content={t('clientDetail.openInXero')} relationship="label">
                  <Button
                    appearance="subtle"
                    icon={<LinkRegular />}
                    size="small"
                    as="a"
                    href={client.cr8f7_xerolink}
                    target="_blank"
                  >
                    {t('clientDetail.xero')}
                  </Button>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className={styles.statsGrid}>
        <Card className={styles.statCard}>
          <span className={styles.statValue} style={{ color: tokens.colorBrandForeground1 }}>
            {countOf(reviewsKnown, reviewsError, clientReviews.length)}
          </span>
          <span className={styles.statLabel}>{t('clientDetail.stat.totalReviews')}</span>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
            {clientReviews.filter(r => r.status === 'completed').length}
          </span>
          <span className={styles.statLabel}>{t('clientDetail.stat.completed')}</span>
        </Card>
        <Card className={styles.statCard}>
          <span
            className={styles.statValue}
            style={{
              color: reviewedThroughIso ? tokens.colorNeutralForeground1 : tokens.colorNeutralForeground3,
              fontSize: tokens.fontSizeBase500,
            }}
          >
            {reviewedThroughIso ? formatCalendarDate(reviewedThroughIso) : t('clientDetail.never')}
          </span>
          <span className={styles.statLabel}>{t('clientDetail.stat.reviewedThrough')}</span>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {outstandingTasks}
          </span>
          <Tooltip content={t('clientDetail.stat.outstandingTooltip', { period: taskPeriodLabel.toLowerCase() })} relationship="label">
            <span className={styles.statLabel}>
              {t('clientDetail.stat.outstanding')}
              <br />
              <span style={{ fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground4 }}>
                {t('clientDetail.stat.open', { period: taskPeriodLabel })}
              </span>
            </span>
          </Tooltip>
        </Card>
        <Card className={styles.statCard}>
          <span className={styles.statValue} style={{ color: tokens.colorPaletteYellowForeground1 }}>
            {clientActionItems.filter(i => i.status !== 'completed').length}
          </span>
          <span className={styles.statLabel}>{t('clientDetail.stat.openActionItems')}</span>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <TabList
          selectedValue={selectedTab}
          onTabSelect={(_, data) => setSelectedTab(data.value as string)}
        >
          <Tab value="overview" icon={<DocumentSearchRegular />}>{t('clientDetail.tab.overview')}</Tab>
          <Tab value="checklist" icon={<CheckboxCheckedRegular />}>{t('clientDetail.tab.checklist')}</Tab>
          <Tab value="reviews" icon={<CheckmarkCircleRegular />}>
            {t('clientDetail.tab.reviews', { n: countOf(reviewsKnown, reviewsError, clientReviews.length) })}
          </Tab>
          <Tab value="action-items" icon={<TaskListLtr24Regular />}>
            {t('clientDetail.tab.actionItems', { n: countOf(actionItemsKnown, actionItemsError, clientActionItems.length) })}
          </Tab>
          <Tab value="tasks" icon={<ClockRegular />}>
            {t('clientDetail.tab.tasks', { n: outstandingTasks + completedTasks })}
          </Tab>
        </TabList>

        <div className={styles.tabContent}>
          {/* Overview Tab */}
          {selectedTab === 'overview' && (
            <div style={{ padding: '16px' }}>
              <Title2 style={{ marginBottom: '16px' }}>{t('clientDetail.overview.title')}</Title2>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>{t('clientDetail.overview.details')}</Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.bookkeepingId')} </Text>
                      <Text>{client.cr8f7_name}</Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.status')} </Text>
                      <Text>{statusLabel}</Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.complexity')} </Text>
                      <Text>{t(`complexity.label.${complexity}`)}</Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.salesTax')} </Text>
                      <Text>{client.cr8f7_salestax ? t('clientDetail.overview.yes') : t('clientDetail.overview.no')}</Text>
                    </div>
                  </div>
                </div>
                <div>
                  <Text weight="semibold" style={{ display: 'block', marginBottom: '4px' }}>{t('clientDetail.overview.taskSummary')}</Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '8px' }}>
                    {t('clientDetail.overview.taskSummarySub', { period: taskPeriodLabel })}
                  </Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.outstanding')} </Text>
                      <Text style={{ color: outstandingTasks > 0 ? tokens.colorPaletteRedForeground1 : 'inherit' }}>
                        {outstandingTasks}
                      </Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.completed')} </Text>
                      <Text style={{ color: tokens.colorPaletteGreenForeground1 }}>
                        {completedTasks}
                      </Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.waiting')} </Text>
                      <Text>{waitingOnClientTasks}</Text>
                    </div>
                    <div>
                      <Text className={styles.infoLabel}>{t('clientDetail.overview.legacy')} </Text>
                      <Tooltip content={t('clientDetail.overview.legacyTooltip')} relationship="label">
                        <Text style={{ color: tokens.colorNeutralForeground3 }}>{legacyOutstandingTasks}</Text>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>
              {client.cr8f7_generalnotes && (
                <div style={{ marginTop: '24px' }}>
                  <Text weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>{t('clientDetail.overview.notes')}</Text>
                  <Text style={{ whiteSpace: 'pre-wrap' }}>{client.cr8f7_generalnotes}</Text>
                </div>
              )}
            </div>
          )}

          {/* Checklist Settings Tab */}
          {selectedTab === 'checklist' && (
            <div style={{ padding: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <Title2 style={{ marginBottom: '8px' }}>{t('clientDetail.checklist.title')}</Title2>
                  <Text style={{ color: tokens.colorNeutralForeground3 }}>
                    {t('clientDetail.checklist.sub')}
                  </Text>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <Text weight="semibold">{t('clientDetail.checklist.reviewType')}</Text>
                  <Dropdown
                    value={checklistReviewType === 'quarterly' ? t('reviewType.quarterly') : t('reviewType.yearEnd')}
                    onOptionSelect={(_, data) => setChecklistReviewType(data.optionValue as 'quarterly' | 'year-end')}
                    style={{ minWidth: '150px' }}
                  >
                    <Option value="quarterly">{t('reviewType.quarterly')}</Option>
                    <Option value="year-end">{t('reviewType.yearEnd')}</Option>
                  </Dropdown>
                </div>
              </div>

              {settingsLoading ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <Spinner label={t('clientDetail.checklist.loadingSettings')} />
                </div>
              ) : settingsError && !clientSettings ? (
                <Card style={{ padding: '24px' }}>
                  <LoadFailed onRetry={() => refetchSettings()} />
                </Card>
              ) : (
                <>
                  {/* Default Checklist Items */}
                  <Card style={{ padding: '16px', marginBottom: '24px' }}>
                    <Text weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
                      {t('clientDetail.checklist.defaultItems', { n: checklistReviewType === 'quarterly' ? 11 : 16 })}
                    </Text>
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '16px' }}>
                      {t('clientDetail.checklist.defaultSub')}
                    </Text>

                    {defaultItems === undefined ? (
                      defaultsError ? (
                        <LoadFailed onRetry={() => refetchDefaults()} />
                      ) : (
                        <Spinner label={t('clientDetail.checklist.loadingDefaults')} />
                      )
                    ) : defaultItems.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* Group by category */}
                        {['reconciliation', 'compliance', 'data-entry', 'reporting', 'year-end'].map(category => {
                          const categoryItems = defaultItems.filter(item => item.category === category);
                          if (categoryItems.length === 0) return null;

                          const currentSettings = checklistReviewType === 'year-end'
                            ? clientSettings?.yearEndChecklistSettings
                            : clientSettings?.quarterlyChecklistSettings;
                          const disabledItems = currentSettings?.disabledDefaultItems || [];

                          return (
                            <div key={category} style={{ marginBottom: '12px' }}>
                              <Text weight="semibold" size={200} style={{ color: tokens.colorNeutralForeground3, marginBottom: '8px', display: 'block' }}>
                                {t(`checklistCategory.${category}`)}
                              </Text>
                              {categoryItems.map(item => {
                                const isEnabled = !disabledItems.includes(item.title);
                                return (
                                  <div
                                    key={item.id}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '8px 12px',
                                      backgroundColor: isEnabled ? tokens.colorNeutralBackground2 : tokens.colorNeutralBackground4,
                                      borderRadius: '4px',
                                      marginBottom: '4px',
                                      opacity: isEnabled ? 1 : 0.6,
                                    }}
                                  >
                                    <Checkbox
                                      checked={isEnabled}
                                      onChange={(_, data) => {
                                        if (id) {
                                          toggleDefaultItemMutation.mutate({
                                            bookkeepingId: id,
                                            data: {
                                              reviewType: checklistReviewType,
                                              itemTitle: item.title,
                                              isEnabled: data.checked as boolean,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                    <div style={{ flex: 1 }}>
                                      <Text style={{ textDecoration: isEnabled ? 'none' : 'line-through' }}>
                                        {item.title}
                                      </Text>
                                      {item.description && (
                                        <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                                          {item.description}
                                        </Text>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <Text style={{ color: tokens.colorNeutralForeground3 }}>{t('clientDetail.checklist.noDefaults')}</Text>
                    )}
                  </Card>

                  {/* Custom Checklist Items */}
                  <Card style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div>
                        <Text weight="semibold" style={{ display: 'block' }}>
                          {t('clientDetail.checklist.customTitle')}
                        </Text>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          {t('clientDetail.checklist.customSub')}
                        </Text>
                      </div>
                      <Button
                        appearance="primary"
                        size="small"
                        icon={<AddRegular />}
                        onClick={() => setShowAddCustomDialog(true)}
                      >
                        {t('clientDetail.checklist.addCustom')}
                      </Button>
                    </div>

                    {(() => {
                      const currentSettings = checklistReviewType === 'year-end'
                        ? clientSettings?.yearEndChecklistSettings
                        : clientSettings?.quarterlyChecklistSettings;
                      const customItems = currentSettings?.customItems || [];

                      if (customItems.length === 0) {
                        return (
                          <div style={{ padding: '24px', textAlign: 'center', color: tokens.colorNeutralForeground3 }}>
                            <Text>{t('clientDetail.checklist.noCustom')}</Text>
                          </div>
                        );
                      }

                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {customItems.map(item => (
                            <div
                              key={item.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                backgroundColor: tokens.colorNeutralBackground2,
                                borderRadius: '4px',
                              }}
                            >
                              <Badge appearance="tint" color="brand" size="small">{t('checklistCategory.custom')}</Badge>
                              <div style={{ flex: 1 }}>
                                <Text>{item.title}</Text>
                                {item.description && (
                                  <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                                    {item.description}
                                  </Text>
                                )}
                              </div>
                              <Tooltip content={t('clientDetail.checklist.deleteCustom')} relationship="label">
                                <Button
                                  appearance="subtle"
                                  size="small"
                                  icon={<DeleteRegular />}
                                  onClick={() => {
                                    if (id) {
                                      deleteCustomItemMutation.mutate({
                                        bookkeepingId: id,
                                        itemId: item.id,
                                        reviewType: checklistReviewType,
                                      });
                                    }
                                  }}
                                />
                              </Tooltip>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </Card>
                </>
              )}

              {/* Add Custom Item Dialog */}
              <Dialog open={showAddCustomDialog} onOpenChange={(_, data) => !data.open && setShowAddCustomDialog(false)}>
                <DialogSurface style={{ maxWidth: '500px' }}>
                  <DialogBody>
                    <DialogTitle>{t('clientDetail.dialog.title')}</DialogTitle>
                    <DialogContent>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Field label={t('clientDetail.dialog.itemTitle')} required>
                          <Input
                            value={newCustomTitle}
                            onChange={(_, data) => setNewCustomTitle(data.value)}
                            placeholder={t('clientDetail.dialog.itemTitlePlaceholder')}
                          />
                        </Field>
                        <Field label={t('clientDetail.dialog.description')}>
                          <Textarea
                            value={newCustomDescription}
                            onChange={(_, data) => setNewCustomDescription(data.value)}
                            placeholder={t('clientDetail.dialog.descriptionPlaceholder')}
                            rows={2}
                          />
                        </Field>
                        <Field label={t('clientDetail.dialog.category')}>
                          <Dropdown
                            value={t(`checklistCategory.${newCustomCategory}`)}
                            onOptionSelect={(_, data) => setNewCustomCategory(data.optionValue as string)}
                          >
                            {['custom', 'reconciliation', 'compliance', 'data-entry', 'reporting'].map((c) => (
                              <Option key={c} value={c}>{t(`checklistCategory.${c}`)}</Option>
                            ))}
                          </Dropdown>
                        </Field>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          {t('clientDetail.dialog.addedTo', { type: checklistReviewType === 'quarterly' ? t('reviewType.quarterly') : t('reviewType.yearEnd') })}
                        </Text>
                      </div>
                    </DialogContent>
                    <DialogActions>
                      <DialogTrigger disableButtonEnhancement>
                        <Button appearance="secondary">{t('common.cancel')}</Button>
                      </DialogTrigger>
                      <Button
                        appearance="primary"
                        disabled={!newCustomTitle.trim() || addCustomItemMutation.isPending}
                        onClick={() => {
                          if (id && newCustomTitle.trim()) {
                            addCustomItemMutation.mutate({
                              bookkeepingId: id,
                              data: {
                                reviewType: checklistReviewType,
                                category: newCustomCategory,
                                title: newCustomTitle.trim(),
                                description: newCustomDescription.trim() || undefined,
                              },
                            }, {
                              onSuccess: () => {
                                setNewCustomTitle('');
                                setNewCustomDescription('');
                                setNewCustomCategory('custom');
                                setShowAddCustomDialog(false);
                              },
                            });
                          }
                        }}
                      >
                        {addCustomItemMutation.isPending ? t('clientDetail.dialog.adding') : t('clientDetail.dialog.add')}
                      </Button>
                    </DialogActions>
                  </DialogBody>
                </DialogSurface>
              </Dialog>
            </div>
          )}

          {/* Reviews Tab */}
          {selectedTab === 'reviews' && (
            <div>
              {reviewsLoading ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <Spinner label={t('clientDetail.reviews.loading')} />
                </div>
              ) : !reviewsKnown && reviewsError ? (
                <Card style={{ padding: '24px' }}>
                  <LoadFailed onRetry={() => refetchReviews()} />
                </Card>
              ) : clientReviews.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>{t('clientDetail.reviews.col.period')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.type')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.status')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.checklist')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.taxAppt')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.language')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.reviews.col.actions')}</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientReviews.map(review => {
                      const checkedCount = review.checklistItems.filter(i => i.isChecked).length;
                      const totalCount = review.checklistItems.length;

                      return (
                        <TableRow key={review.id} className={ROW_LINK_HOST} style={{ cursor: 'pointer' }}>
                          <TableCell>
                            {/* The whole row is this link: right-click anywhere on it opens the review in a new tab. */}
                            <RowLink href={`/reviews/${review.id}`}>
                              {formatPeriodRange(review.periodStart, review.periodEnd)}
                            </RowLink>
                          </TableCell>
                          <TableCell>
                            <Badge appearance="outline">
                              {review.reviewType === 'year-end' ? t('reviewType.yearEnd') : t('reviewType.quarterly')}
                            </Badge>
                          </TableCell>
                          <TableCell>{getReviewStatusBadge(review.status)}</TableCell>
                          <TableCell>
                            <Text size={200}>
                              {checkedCount}/{totalCount}
                            </Text>
                          </TableCell>
                          <TableCell>
                            {review.nextTaxAppointment ? (
                              <Tooltip content={formatInstant(review.nextTaxAppointment, 'dayYearTime')} relationship="label">
                                <Text size={200}>
                                  {formatInstant(review.nextTaxAppointment, 'monthDay')}
                                </Text>
                              </Tooltip>
                            ) : (
                              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>-</Text>
                            )}
                          </TableCell>
                          <TableCell>
                            <ClientLanguageBadge language={review.clientLanguage} />
                          </TableCell>
                          <TableCell>
                            <Button
                              as="a"
                              appearance="subtle"
                              icon={<OpenRegular />}
                              size="small"
                              aria-label={t('clientDetail.reviews.open', { period: formatPeriodRange(review.periodStart, review.periodEnd) })}
                              {...realLinkProps(`/reviews/${review.id}`, { navigate: routerNavigate })}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className={styles.emptyState}>
                  <DocumentSearchRegular style={{ fontSize: '48px' }} />
                  <Text style={{ display: 'block', marginTop: '16px' }}>
                    {t('clientDetail.reviews.none')}
                  </Text>
                  <Button
                    as="a"
                    appearance="primary"
                    style={{ marginTop: '16px' }}
                    {...reviewsLink}
                  >
                    {t('clientDetail.reviews.create')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Action Items Tab */}
          {selectedTab === 'action-items' && (
            <div>
              {actionItemsLoading ? (
                <div style={{ padding: '48px', textAlign: 'center' }}>
                  <Spinner label={t('clientDetail.actions.loading')} />
                </div>
              ) : !actionItemsKnown && actionItemsError ? (
                <Card style={{ padding: '24px' }}>
                  <LoadFailed onRetry={() => refetchActionItems()} />
                </Card>
              ) : clientActionItems.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>{t('clientDetail.actions.col.title')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.actions.col.priority')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.actions.col.status')}</TableHeaderCell>
                      <TableHeaderCell>{t('clientDetail.actions.col.due')}</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clientActionItems.map(item => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <Text>{item.title}</Text>
                            {item.carryForwardCount > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: tokens.colorPaletteRedForeground1, fontSize: tokens.fontSizeBase200 }}>
                                <AlertRegular fontSize={12} />
                                {t('clientDetail.actions.carried', { n: item.carryForwardCount })}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            appearance={item.priority === 'critical' ? 'filled' : 'tint'}
                            color={
                              item.priority === 'critical' ? 'danger' :
                              item.priority === 'high' ? 'danger' :
                              item.priority === 'medium' ? 'warning' : 'subtle'
                            }
                          >
                            {t(`actionItems.priority.${item.priority}`)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            appearance={item.status === 'completed' ? 'filled' : 'outline'}
                            color={
                              item.status === 'completed' ? 'success' :
                              item.status === 'blocked' ? 'danger' :
                              item.status === 'in-progress' ? 'warning' : 'informative'
                            }
                          >
                            {actionItemStatusLabel(item.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {item.dueDate ? (
                            <Text size={200}>{formatCalendarDate(item.dueDate, { withYear: false })}</Text>
                          ) : (
                            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>-</Text>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className={styles.emptyState}>
                  <TaskListLtr24Regular style={{ fontSize: '48px' }} />
                  <Text style={{ display: 'block', marginTop: '16px' }}>
                    {t('clientDetail.actions.none')}
                  </Text>
                </div>
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {selectedTab === 'tasks' && (
            <div style={{ padding: '24px' }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: '12px' }}>
                {t('clientDetail.tasks.liveNote', { period: taskPeriodLabel })}
              </Text>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <Card style={{ padding: '16px', textAlign: 'center' }}>
                  <Text size={600} weight="bold" style={{ color: tokens.colorPaletteRedForeground1, display: 'block' }}>
                    {outstandingTasks}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('clientDetail.tasks.outstandingOpen')}</Text>
                </Card>
                <Card style={{ padding: '16px', textAlign: 'center' }}>
                  <Text size={600} weight="bold" style={{ color: tokens.colorPaletteYellowForeground1, display: 'block' }}>
                    {waitingOnClientTasks}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('clientDetail.tasks.waiting')}</Text>
                </Card>
                <Card style={{ padding: '16px', textAlign: 'center' }}>
                  <Text size={600} weight="bold" style={{ color: tokens.colorPaletteGreenForeground1, display: 'block' }}>
                    {completedTasks}
                  </Text>
                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{t('clientDetail.tasks.completed')}</Text>
                </Card>
              </div>
              <div className={styles.emptyState}>
                <ClockRegular style={{ fontSize: '48px' }} />
                <Text style={{ display: 'block', marginTop: '16px' }}>
                  {t('clientDetail.tasks.synced')}
                </Text>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

export default ClientDetailPage;
